import { Form } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { FailReportDataUpdateItem } from '@/model/FailReport/FailReport';
import { PRODUCT_TYPES, ProductTypeStrings } from '@/model/Logistic/PackageItem/ProductTypes';

/** Elemento simple asociado a la falla del ticket. */
export interface TicketFailItem {
  key: string;
  product_type: PRODUCT_TYPES;
  // El producto siempre se asigna; device/unidad son adicionales y solo aplican
  // cuando product_type es DEVICE/UNIT (no reemplazan a product).
  product: number;
  device?: number;
  unidad?: number;
  name: string;
  serial_number?: string;
  comment?: string;
}

/**
 * Hook que encapsula la lógica de la tabla inline de elementos involucrados en la falla.
 * Maneja el estado de los items, la fila de creación y la sincronización con el form padre.
 */
const useTicketFormFailInfo = (initialItems?: TicketFailItem[]) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const [items, setItems] = useState<TicketFailItem[]>(initialItems ?? []);
  // Si los items iniciales (reporte automático ya existente) llegan después del primer
  // render, los aplicamos una sola vez sin pisar lo que el usuario ya haya tocado.
  const hasDoneInitialSync = useRef(Boolean(initialItems?.length));

  useEffect(() => {
    if (!hasDoneInitialSync.current && initialItems && initialItems.length > 0) {
      setItems(initialItems);
      hasDoneInitialSync.current = true;
    }
  }, [initialItems]);
  // Estado para la fila inline de nuevo elemento
  const [newProductType, setNewProductType] = useState<PRODUCT_TYPES | undefined>(undefined);
  const [newProduct, setNewProduct] = useState<number | undefined>(undefined);
  const [newProductLabel, setNewProductLabel] = useState('');
  // Id real del elemento serializado (device/unidad) y su etiqueta para mostrar en la tabla,
  // por separado: el id no debe pisar a `product`, solo acompañarlo.
  const [newSerialNumberValue, setNewSerialNumberValue] = useState<number | undefined>(undefined);
  const [newSerialNumberLabel, setNewSerialNumberLabel] = useState<string | undefined>(undefined);
  const [newComments, setNewComments] = useState('');
  // Indica si el tipo seleccionado requiere número de serie
  const needsSerialNumber =
    newProductType === PRODUCT_TYPES.UNIT || newProductType === PRODUCT_TYPES.DEVICE;
  // Indica si alguno de los items cargados tiene número de serie (para mostrar columna)
  const showSerialColumn = useMemo(
    () => needsSerialNumber || items.some(i => i.serial_number),
    [items, needsSerialNumber],
  );
  // Select de productos según el tipo seleccionado
  const { selectProps: productsSelectProps } = useSelectScrollSearch({
    resource: 'elements',
    optionLabel: (item: any) => item.name,
    optionValue: (item: any) => item.id,
    queryOptions: { enabled: newProductType !== undefined },
    filters: [{ value: newProductType, operator: 'eq' as const, field: 'type' }],
  });
  // Select de números de serie (solo para unidades y dispositivos)
  const { selectProps: serialNumbersSelectProps } = useSelectScrollSearch({
    resource: 'elements',
    optionLabel: (item: any) => item.name,
    optionValue: (item: any) =>
      newProductType === PRODUCT_TYPES.UNIT ? item.serial_number_box : item.id,
    queryOptions: {
      enabled: needsSerialNumber,
      queryKey: ['elements', { type: newProductType, serializable: true }],
    },
    filters: [
      { value: newProductType, operator: 'eq' as const, field: 'type' },
      { value: true, operator: 'eq' as const, field: 'serializable' },
    ],
  });
  // Opciones de tipo de producto
  const productTypeOptions = useMemo(
    () => Object.values(ProductTypeStrings).map((v, index) => ({ label: t(v), value: index })),
    [t],
  );
  // Sincroniza los items al campo oculto del form, mapeados al shape que espera
  // `fail_report_data_update.fail_report_items`: product siempre va, device/unidad
  // son adicionales (ya vienen resueltos en el item, no se derivan de product_type).
  const failReportItemsPayload = useMemo<FailReportDataUpdateItem[]>(
    () =>
      items.map(({ product, device, unidad, comment }) => ({
        product,
        device: device ?? null,
        unidad: unidad ?? null,
        comment: comment || undefined,
      })),
    [items],
  );

  useEffect(() => {
    form?.setFieldValue('fail_report_items', failReportItemsPayload);
  }, [failReportItemsPayload, form]);

  const resetNewRow = useCallback(() => {
    setNewProductType(undefined);
    setNewProduct(undefined);
    setNewProductLabel('');
    setNewSerialNumberValue(undefined);
    setNewSerialNumberLabel(undefined);
    setNewComments('');
  }, []);
  const handleAdd = useCallback(() => {
    if (newProduct === undefined || newProductType === undefined) return;
    const item: TicketFailItem = {
      key: Date.now().toString(),
      product_type: newProductType,
      product: newProduct,
      device: newProductType === PRODUCT_TYPES.DEVICE ? newSerialNumberValue : undefined,
      unidad: newProductType === PRODUCT_TYPES.UNIT ? newSerialNumberValue : undefined,
      name: newProductLabel,
      serial_number: newSerialNumberLabel,
      comment: newComments || undefined,
    };

    setItems(prev => [...prev, item]);
    resetNewRow();
  }, [
    newProduct,
    newProductType,
    newProductLabel,
    newSerialNumberValue,
    newSerialNumberLabel,
    newComments,
    resetNewRow,
  ]);
  const handleDelete = useCallback((key: string) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);
  const handleProductTypeChange = useCallback((value: PRODUCT_TYPES | undefined) => {
    setNewProductType(value);
    setNewProduct(undefined);
    setNewProductLabel('');
    setNewSerialNumberValue(undefined);
    setNewSerialNumberLabel(undefined);
  }, []);
  const handleProductChange = useCallback((value: number | undefined, opt: any) => {
    setNewProduct(value);
    if (!Array.isArray(opt)) {
      setNewProductLabel((opt?.label as string) ?? '');
    }
  }, []);
  const handleSerialNumberChange = useCallback((value: number | undefined, opt: any) => {
    setNewSerialNumberValue(value);
    setNewSerialNumberLabel(
      !Array.isArray(opt) ? ((opt?.label as string) ?? undefined) : undefined,
    );
  }, []);

  return {
    items,
    newProductType,
    newProduct,
    newSerialNumberValue,
    newComments,
    setNewComments,
    needsSerialNumber,
    showSerialColumn,
    productsSelectProps: productsSelectProps,
    serialNumbersSelectProps: serialNumbersSelectProps,
    productTypeOptions,
    handleAdd,
    handleDelete,
    handleProductTypeChange,
    handleProductChange,
    handleSerialNumberChange,
  };
};

export default useTicketFormFailInfo;
